import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { Observable, catchError, finalize, map, of } from 'rxjs';
import { GetAnnualReportTemplateTranslation } from '../dtos/get-annual-report-template-translation.dto';
import { IAnnualReportTemplateDTO } from '../interfaces/annual-report-template-dto.interface';
import { IARTemplateFilterCriteria } from '../interfaces/annual-report-template-filter-criteria.interface';
import { UpdateAnnualReportTemplateBalanceTaxonomyIdDto } from '../types/update-annual-report-template-balance-taxonomy-dto.type';
import { UpdateAnnualReportTemplateIncomeTaxonomyIdDto } from '../types/update-annual-report-template-income-taxonomy-dto';
import { UpdateAnnualReportTemplateNotesTemplateIdDto } from '../types/update-annual-report-template-notes-template-dto.type';
import { UpdateAnnualReportTemplatePdfTemplateIdDto } from '../types/update-annual-report-template-pdf-template-dto.type';
import { UpdateAnnualReportTemplateSettingsDto } from '../types/update-annual-report-template-settings-dto.type';
import { RoutesUtil } from '../utils/routes.util';

@Injectable({
    providedIn: 'root',
})
export class AumicoAnnualReportTemplatesService {
    constructor(
        private readonly http: HttpClient,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly routesUtil: RoutesUtil,
    ) {}

    createArTemplate$(arTemplate: IAnnualReportTemplateDTO): Observable<IAnnualReportTemplateDTO> {
        const url = this.routesUtil.createArTemplate();
        this.coreProvider.dispatchBlockingRequest('createArTemplate$');
        return this.http
            .post<IAnnualReportTemplateDTO>(url, arTemplate)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createArTemplate$')));
    }

    updateArTemplate$(
        id: string,
        updatedPartialArTemplate:
            | IAnnualReportTemplateDTO
            | UpdateAnnualReportTemplateIncomeTaxonomyIdDto
            | UpdateAnnualReportTemplateBalanceTaxonomyIdDto
            | UpdateAnnualReportTemplatePdfTemplateIdDto
            | UpdateAnnualReportTemplateNotesTemplateIdDto
            | UpdateAnnualReportTemplateSettingsDto,
    ): Observable<IAnnualReportTemplateDTO> {
        const url = this.routesUtil.updateArTemplateById(id);
        this.coreProvider.dispatchBlockingRequest('updateArTemplate$');
        return this.http
            .patch<IAnnualReportTemplateDTO>(url, updatedPartialArTemplate)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateArTemplate$')));
    }

    deleteArTemplate$(id: string): Observable<string> {
        const url = this.routesUtil.deleteArTemplate(id);
        this.coreProvider.dispatchBlockingRequest('deleteArTemplate$');
        return this.http
            .delete<string>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deleteArTemplate$')));
    }

    getAllArTemplates$(): Observable<IAnnualReportTemplateDTO[]> {
        const url = this.routesUtil.getAllArTemplates();
        this.coreProvider.dispatchBlockingRequest('getAllArTemplates$');
        return this.http
            .get<IAnnualReportTemplateDTO[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAllArTemplates$')));
    }

    getArTemplateById$(id: string): Observable<IAnnualReportTemplateDTO> {
        const url = this.routesUtil.getArTemplateById(id);
        return this.http
            .get<IAnnualReportTemplateDTO>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getArTemplateById$')));
    }

    downloadDefaultPdfTemplate$(): Observable<any> {
        const url = this.routesUtil.downloadDefaultPdfTemplate();
        this.coreProvider.dispatchBlockingRequest('downloadDefaultPdfTemplate$');
        return this.http
            .get(url, { responseType: 'blob' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('downloadDefaultPdfTemplate$')));
    }

    downloadDefaultHgbPdfTemplate$(): Observable<any> {
        const url = this.routesUtil.downloadDefaultHgbPdfTemplate();
        this.coreProvider.dispatchBlockingRequest('downloadDefaultHgbPdfTemplate$');
        return this.http
            .get(url, { responseType: 'blob' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('downloadDefaultHgbPdfTemplate$')));
    }

    downloadPdfTemplate$(id: string): Observable<any> {
        const url = this.routesUtil.downloadPdfTemplate(id);
        this.coreProvider.dispatchBlockingRequest('downloadPdfTemplate$');
        return this.http
            .get(url, { responseType: 'blob' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('downloadPdfTemplate$')));
    }

    uploadPdfTemplate$(file: any): Observable<string> {
        const url = this.routesUtil.uploadPdfTemplate();
        this.coreProvider.dispatchBlockingRequest('uploadPdfTemplate$');
        return this.http.post(url, file, { responseType: 'text', observe: 'response' }).pipe(
            map((response: HttpResponse<string>) => response.body),
            catchError((error: any) => {
                return of(error);
            }),
            finalize(() => this.coreProvider.dispatchBlockingRequestEnd('uploadPdfTemplate$')),
        );
    }

    getArTemplatesByFilter$(filterCriteria: IARTemplateFilterCriteria): Observable<IAnnualReportTemplateDTO[]> {
        const url = this.routesUtil.getAllArTemplates();

        const params = new HttpParams()
            .set('legalFormId', filterCriteria.legalFormId)
            .set('countryISO31661Alpha2Code', filterCriteria.countryISO31661Alpha2Code);

        this.coreProvider.dispatchBlockingRequest('getArTemplatesByLegalFormAndCountry$');
        return this.http
            .get<IAnnualReportTemplateDTO[]>(url, { params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getArTemplatesByLegalFormAndCountry$')));
    }

    getArTemplateLocales$(id: string): Observable<string[]> {
        const url = this.routesUtil.getArTemplateLocales(id);

        this.coreProvider.dispatchBlockingRequest('getArTemplateLocales$');
        return this.http
            .get<string[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getArTemplateLocales$')));
    }

    public getTranslations(locales: string[], untranslated: boolean): Observable<GetAnnualReportTemplateTranslation[]> {
        let url = this.routesUtil.getArTemplatesTranslations();

        const queryParams = `locales=${locales.join(',')}&untranslated=${untranslated}`;
        const urlWithParams = `${url}?${queryParams}`;

        this.coreProvider.dispatchBlockingRequest('getArTemplatesTranslations');
        return this.http
            .get<GetAnnualReportTemplateTranslation[]>(urlWithParams)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getArTemplatesTranslations')));
    }

    public updateTranslations(translations: GetAnnualReportTemplateTranslation[]): Observable<void> {
        const url = this.routesUtil.getArTemplatesTranslations();
        this.coreProvider.dispatchBlockingRequest('updateArTemplatesTranslations');
        return this.http
            .patch<void>(url, translations)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateArTemplatesTranslations')));
    }
}
